import { Link, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { compact, bytes } from '../components/ModelSpecs';
import { TypewriterText } from '../hooks/useTypewriter';
import Verdict from '../components/Verdict';
import NowRows, { nowItems } from '../components/NowRows';

const API = '/api';


function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d`;
  const mo = Math.floor(d / 30); if (mo < 12) return `${mo}mo`;
  return `${Math.floor(mo / 12)}y`;
}

const TABS = [
  { key: 'recent', label: 'recent' },
  { key: 'following', label: 'following' },
];

export default function Home() {
  const { user, login } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'following' ? 'following' : 'recent';
  const [tab, setTab] = useState(initialTab);
  const [recent, setRecent] = useState(null);
  const [following, setFollowing] = useState(null);
  const [loading, setLoading] = useState(false);

  const [mine, setMine] = useState(null);

  // your runs, live: one line each at the top, macs and cloud
  useEffect(() => {
    if (!user) { setMine(null); return undefined; }
    const token = localStorage.getItem('logos_session_token');
    const load = () => fetch(`${API}/me/workspace`, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => (r.ok ? r.json() : null)).then(d => { if (d) setMine(d); }).catch(() => {});
    load();
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
  }, [user]);
  const running = nowItems(mine, { trainingOnly: true });

  const switchTab = (t) => {
    setTab(t);
    const next = new URLSearchParams(searchParams);
    if (t === 'following') next.set('tab', 'following'); else next.delete('tab');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (tab === 'recent' && recent === null) {
      setLoading(true);
      Promise.all([
        fetch(`${API}/checkpoints?sort=newest&limit=40`).then(r => (r.ok ? r.json() : [])).catch(() => []),
        fetch(`${API}/developments?limit=40`).then(r => (r.ok ? r.json() : { developments: [] }))
          .then(d => d.developments || []).catch(() => []),
      ])
        .then(([checkpoints, developments]) => {
          const merged = [
            ...checkpoints.map(cp => ({ ...cp, feed_type: 'checkpoint', feed_at: cp.created_at })),
            ...developments.map(d => ({ ...d, feed_type: 'development', feed_at: d.published_at })),
          ];
          merged.sort((a, b) => (b.feed_at || '').localeCompare(a.feed_at || ''));
          setRecent(merged.slice(0, 40));
        })
        .finally(() => setLoading(false));
    } else if (tab === 'following' && user && following === null) {
      setLoading(true);
      const token = localStorage.getItem('logos_session_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      fetch(`${API}/feed/following?limit=40`, { credentials: 'include', headers })
        .then(r => (r.ok ? r.json() : []))
        .then(setFollowing)
        .finally(() => setLoading(false));
    }
  }, [tab, user, recent, following]);

  const items = tab === 'recent' ? recent : following;

  return (
    <div className="min-h-screen bg-black page-wrapper" data-testid="home-page">
      {/* Hero — right-aligned, terse */}
      <section className="pt-10 lg:pt-16 pb-8 px-6 lg:px-8" aria-label="hero">
        <div className="max-w-3xl mx-auto text-right">
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight leading-none logo-text">
            <TypewriterText text="logOS." speed={90} startDelay={200} />
          </h1>
          <p className="text-[#888888] lowercase text-sm lg:text-base mt-3">
            where minds meet.
          </p>
        </div>
      </section>

      {running.length > 0 && (
        <section className="pb-8 px-6 lg:px-8" aria-label="your runs" data-testid="home-running">
          <div className="max-w-3xl mx-auto"><NowRows items={running} /></div>
        </section>
      )}

      {/* Orientation strip — three-step journey */}
      <section className="px-6 lg:px-8 border-t border-[#151b17]" aria-label="orientation">
        <div className="max-w-3xl mx-auto py-2 grid grid-cols-1 sm:grid-cols-3 sm:divide-x divide-y sm:divide-y-0 divide-[#1a201b] text-sm" data-testid="orientation-strip">
          <Link to="/downloads" className="group py-4 sm:px-5 sm:first:pl-0 sm:last:pr-0" data-testid="orient-download">
            <p className="text-[#444444] font-mono text-[10px] lowercase mb-1">01 · get it</p>
            <p className="text-white lowercase">download soma</p>
            <p className="text-[#666666] lowercase text-xs mt-0.5 group-hover:text-[#aaaaaa] transition-colors">mac app or script</p>
          </Link>
          <Link to="/about" className="group py-4 sm:px-5 sm:first:pl-0 sm:last:pr-0" data-testid="orient-train">
            <p className="text-[#444444] font-mono text-[10px] lowercase mb-1">02 · train it</p>
            <p className="text-white lowercase">train it on any text</p>
            <p className="text-[#666666] lowercase text-xs mt-0.5 group-hover:text-[#aaaaaa] transition-colors">fixed memory · no context window</p>
          </Link>
          <Link to="/upload" className="group py-4 sm:px-5 sm:first:pl-0 sm:last:pr-0" data-testid="orient-upload">
            <p className="text-[#444444] font-mono text-[10px] lowercase mb-1">03 · share it</p>
            <p className="text-white lowercase">upload a model</p>
            <p className="text-[#666666] lowercase text-xs mt-0.5 group-hover:text-[#aaaaaa] transition-colors">private, unlisted or public · with its history</p>
          </Link>
        </div>
      </section>

      {/* Tabs */}
      <section className="px-6 lg:px-8 border-t border-[#151b17]" aria-label="feed tabs">
        <div className="max-w-3xl mx-auto flex items-center gap-6 py-4">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => switchTab(t.key)}
              className={`lowercase text-sm transition-colors duration-200 ${
                tab === t.key ? 'text-white' : 'text-[#555555] hover:text-[#999999]'
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
              {tab === t.key && <span className="block h-px bg-white mt-1.5" />}
            </button>
          ))}
        </div>
      </section>

      {/* Feed */}
      <section className="pb-16 px-6 lg:px-8 border-t border-[#151b17]" aria-label="feed">
        <div className="max-w-3xl mx-auto">
          {tab === 'following' && !user ? (
            <div className="py-16 text-center space-y-3" data-testid="following-login-prompt">
              <p className="text-white lowercase text-lg">log in to see your following feed.</p>
              <button
                onClick={login}
                className="text-[#888888] lowercase text-sm hover:text-white transition-colors underline-offset-4 underline decoration-[#333333]"
                data-testid="following-login-btn"
              >
                login with google
              </button>
            </div>
          ) : loading || items === null ? (
            <p className="text-[#555555] lowercase text-sm animate-pulse py-8">loading…</p>
          ) : items.length === 0 ? (
            <div className="py-16 text-center space-y-3" data-testid="empty-feed">
              {tab === 'recent' ? (
                <>
                  <p className="text-white lowercase text-lg">the feed is quiet.</p>
                  <p className="text-[#555555] lowercase text-sm">
                    no uploads yet · <Link to="/upload" className="hover:text-white transition-colors underline-offset-4">be the first</Link>
                  </p>
                </>
              ) : (
                <>
                  <p className="text-white lowercase text-lg">nothing yet.</p>
                  <p className="text-[#555555] lowercase text-sm">
                    you're not following anyone yet · <Link to="/checkpoints?tab=creators" className="hover:text-white transition-colors underline-offset-4">find creators</Link>
                  </p>
                </>
              )}
            </div>
          ) : (
            <ol className="divide-y divide-[#1a201b]" data-testid="upload-feed">
              {items.map((item) => (
                item.feed_type === 'development'
                  ? <DevelopmentRow key={`d-${item.slug}`} d={item} />
                  : <UploadRow key={item.checkpoint_id} cp={item} />
              ))}
            </ol>
          )}
        </div>
      </section>
    </div>
  );
}

function DevelopmentRow({ d }) {
  return (
    <li className="py-6 group" data-testid={`development-row-${d.slug}`}>
      <Link to={`/developments/${d.slug}`} className="block">
        <div className="flex items-baseline gap-2 font-mono text-[11px] lowercase text-[#666666]">
          <Verdict verdict={d.verdict} />
          <span>{d.kind}</span>
          {d.thread && <span>· {d.thread}</span>}
          <span className="ml-auto tabular-nums">{timeAgo(d.published_at)}</span>
        </div>
        <p className="font-mono text-white lowercase text-lg lg:text-xl mt-2 group-hover:underline underline-offset-4 decoration-[#444444]">
          {d.title}
        </p>
        {d.summary && <p className="text-[#888888] text-sm mt-1.5 leading-relaxed line-clamp-2">{d.summary}</p>}
        <p className="font-mono text-[11px] text-[#555555] lowercase mt-2">
          {(d.authors && d.authors[0]) || 'logOS'} · {d.version > 1 ? `revised · v${d.version}` : 'development'}
        </p>
      </Link>
    </li>
  );
}

function UploadRow({ cp }) {
  const stats = [
    cp.parameters ? `${compact(cp.parameters)} params` : null,
    `${bytes(cp.bytes_seen || 0)} seen`,
    (cp.runtime_id || cp.soma_version || 'soma').replace(/^soma\./, '').toLowerCase(),
    cp.star_count > 0 ? `${cp.star_count} ★` : null,
    cp.download_count > 0 ? `${cp.download_count.toLocaleString()} ↓` : null,
  ].filter(Boolean);
  return (
    <li className="py-6 group" data-testid={`upload-row-${cp.checkpoint_id}`}>
      <div className="flex items-baseline gap-2 font-mono text-[11px] lowercase text-[#666666]">
        <Link to={`/u/${cp.creator_username}`} className="text-[#aaaaaa] hover:text-white">{cp.creator_username}</Link>
        {cp.creator_verified && <img src="/phi-logo-64.png" alt="verified" title="verified" className="verified-badge" />}
        <span className="ml-auto tabular-nums">{timeAgo(cp.created_at)}</span>
      </div>
      <Link to={`/checkpoints/${cp.checkpoint_id}`} className="block">
        <p className="font-mono text-white lowercase text-lg lg:text-xl mt-2 truncate group-hover:underline underline-offset-4 decoration-[#444444]">
          {cp.name}
        </p>
        <p className="font-mono text-xs text-[#888888] mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
          {stats.map((t, i) => <span key={i}>{t}</span>)}
        </p>
        {cp.corpus_tags && cp.corpus_tags.length > 0 && (
          <p className="font-mono text-[11px] text-[#555555] lowercase mt-2 flex flex-wrap gap-x-3">
            {cp.corpus_tags.slice(0, 6).map(t => <span key={t}>#{t}</span>)}
          </p>
        )}
      </Link>
    </li>
  );
}
